import { Plus, X, PackagePlus } from "lucide-react";
import { useMemo } from "react";

import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomButton from "@/components/desktop/button/CustomButton";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

import { APPLICATION_ORDER_TYPES } from "@/constants/applicationOrderTypes";

import PhytosanitaryApplicationProductRow from "./PhytosanitaryApplicationProductRow";
import PhytosanitaryApplicationNewHeader from "./PhytosanitaryApplicationNewHeader";

import styles from "./PhytosanitaryApplicationForm.module.scss";

const PhytosanitaryApplicationForm = ({
	open,
	onClose,
	lookups,
	mode,
	isIrrigation,
	applicationFormRow,
	formState,
	formErrors,
	updateHeaderField,
	addGroup,
	removeGroup,
	addProductToGroup,
	removeProductFromGroup,
	addAlternativeToGroup,
	removeAlternativeFromGroup,
	updateProduct,
	updateAlternative,
	onSubmit,
	isLoading,
}) => {
	const isAddProductMode = mode === "add_product";
	const modalTitle = isAddProductMode ? "Agregar Producto a Aplicación" : "Nueva Aplicación Fitosanitaria";

	const filteredLookups = useMemo(() => {
		if (!isAddProductMode || !lookups?.products_customers) return lookups;
		const estateParentId = applicationFormRow?.estate_parent_id ?? 0;

		const readOnlyProductIDs = new Set();
		(formState?.groups || []).forEach((group) => {
			[...(group.products || []), ...(group.alternatives || [])].forEach((product) => {
				if (product.isReadOnly && product.productID != null) {
					readOnlyProductIDs.add(product.productID);
				}
			});
		});

		const filtered = lookups.products_customers.filter(
			(product) => product.estate_parent_id === 0 || product.estate_parent_id === estateParentId || readOnlyProductIDs.has(product.value),
		);
		return { ...lookups, products_customers: filtered };
	}, [isAddProductMode, lookups, applicationFormRow, formState?.groups]);

	const filteredCropDetails = useMemo(() => {
		if (!formState.newProgramCropCustomerID || !lookups?.crop_details) return [];
		return lookups.crop_details.filter((detail) => detail.crop_customer_id === formState.newProgramCropCustomerID);
	}, [formState.newProgramCropCustomerID, lookups]);

	const doseLabel = isIrrigation ? "Dosis/Ha" : "Dosis/100L";

	const renderProductRowsHeader = () => (
		<div className={styles.productRowsHeader}>
			<span style={{ flex: "0 0 14%" }}>Objetivo</span>
			<span style={{ flex: "0 0 18%" }}>Producto</span>
			<span style={{ flex: "0 0 9%" }}>Y/O</span>
			<span style={{ flex: "0 0 9%" }}>{doseLabel}</span>
			<span style={{ flex: "1 1 auto" }}>Obs.</span>
			<span style={{ width: 24 }}></span>
		</div>
	);

	const phenologicalStateLabel = (lookups?.phenological_states || []).find((option) => option.value === formState.phenologicalStateID)?.label || "—";

	return (
		<CustomModalForm
			open={open}
			onClose={onClose}
			title={modalTitle}
			size="xxl"
			isLoading={isLoading}
			gridTable={false}
			textForm={isAddProductMode ? "Agregar Producto" : "Crear Aplicación"}
			onSubmit={onSubmit}
		>
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<span className={styles.sectionIcon}>≡</span>
					<strong>Información General</strong>
				</div>

				{isAddProductMode ? (
					<div className={styles.readonlyHeader}>
						{applicationFormRow?.application_order_type_name && (
							<>
								<CustomBadge variant={APPLICATION_ORDER_TYPES[applicationFormRow.application_order_type_id] || "neutral"} size="sm">
									{applicationFormRow.application_order_type_name}
								</CustomBadge>
								<span className={styles.readonlySep}>•</span>
							</>
						)}
						{applicationFormRow?.crop_text && (
							<>
								<span className={styles.readonlyItem}>
									<span className={styles.readonlyLabel}>Especie</span>
									<strong>{applicationFormRow.crop_text}</strong>
								</span>
								<span className={styles.readonlySep}>•</span>
							</>
						)}
						{applicationFormRow?.crop_detail_text && (
							<>
								<span className={styles.readonlyItem}>
									<span className={styles.readonlyLabel}>Variedad</span>
									<strong>{applicationFormRow.crop_detail_text}</strong>
								</span>
								<span className={styles.readonlySep}>•</span>
							</>
						)}
						<span className={styles.readonlyItem}>
							<span className={styles.readonlyLabel}>Aplicación Nº</span>
							<strong>{formState.applicationNumber || "—"}</strong>
						</span>
						<span className={styles.readonlySep}>•</span>
						<span className={styles.readonlyItem}>
							<span className={styles.readonlyLabel}>Época</span>
							<strong>{formState.periodText || "—"}</strong>
						</span>
						<span className={styles.readonlySep}>•</span>
						<span className={styles.readonlyItem}>
							<span className={styles.readonlyLabel}>Estado Fenológico</span>
							<strong>{phenologicalStateLabel}</strong>
						</span>
						{!isIrrigation && formState.wetness != null && (
							<>
								<span className={styles.readonlySep}>•</span>
								<span className={styles.readonlyItem}>
									<span className={styles.readonlyLabel}>Mojamiento</span>
									<strong>{formState.wetness} L/Ha</strong>
								</span>
							</>
						)}
					</div>
				) : (
					<PhytosanitaryApplicationNewHeader
						formState={formState}
						formErrors={formErrors}
						updateHeaderField={updateHeaderField}
						lookups={lookups}
						filteredCropDetails={filteredCropDetails}
						isIrrigation={isIrrigation}
					/>
				)}
			</div>

			<div className={styles.section}>
				<div className={styles.groupsHeader}>
					<div className={styles.groupsTitle}>
						<span className={styles.sectionIcon}>≡</span>
						<strong>Grupos de Productos</strong>
						<span className={styles.groupsSubtitle}>(Los grupos pueden aplicarse juntos o ser alternativas entre sí)</span>
					</div>
					<CustomButton variant="ghost" size="sm" startIcon={<Plus size={14} />} onClick={addGroup} type="button">
						Agregar Grupo
					</CustomButton>
				</div>

				{formErrors.groups && <p className={styles.errorText}>{formErrors.groups}</p>}

				<div className={styles.groupsList}>
					{formState.groups.map((group, groupIndex) => {
						const isExistingGroup = group.subGroupKey !== null;
						const editableProducts = group.products.filter((p) => !p.isReadOnly);
						const editableAlternatives = group.alternatives.filter((a) => !a.isReadOnly);
						const hasReadOnlyProducts = group.products.some((p) => p.isReadOnly);
						const hasReadOnlyAlternatives = group.alternatives.some((a) => a.isReadOnly);
						return (
							<div key={group.id} className={styles.group}>
								<div className={styles.groupHeader}>
									{isExistingGroup ? (
										<CustomBadge variant="success">Grupo {groupIndex + 1}</CustomBadge>
									) : (
										<CustomBadge variant="info">Nuevo Grupo</CustomBadge>
									)}
									<div className={styles.groupActions}>
										<CustomButton
											variant="ghost"
											size="sm"
											startIcon={<Plus size={13} />}
											onClick={() => addProductToGroup(group.id)}
											type="button"
										>
											Agregar Producto
										</CustomButton>
										<CustomButton
											variant="info_outline"
											size="sm"
											startIcon={<PackagePlus size={13} />}
											onClick={() => addAlternativeToGroup(group.id)}
											type="button"
										>
											Grupo alternativo
										</CustomButton>
										{!isExistingGroup && formState.groups.length > 1 && (
											<button
												type="button"
												className={styles.removeGroupBtn}
												onClick={() => removeGroup(group.id)}
												aria-label="Eliminar grupo"
											>
												<X size={14} />
											</button>
										)}
									</div>
								</div>

								{renderProductRowsHeader()}

								{group.products.map((product) => (
									<PhytosanitaryApplicationProductRow
										key={product.id}
										readOnly={product.isReadOnly || false}
										product={product}
										lookups={filteredLookups}
										onUpdate={product.isReadOnly ? () => {} : (field, value) => updateProduct(group.id, product.id, field, value)}
										onRemove={product.isReadOnly ? () => {} : () => removeProductFromGroup(group.id, product.id)}
										showRemove={!product.isReadOnly && (hasReadOnlyProducts || editableProducts.length > 1)}
										isIrrigation={isIrrigation}
									/>
								))}

								{group.alternatives.length > 0 && (
									<div className={styles.alternativeGroup}>
										<div className={styles.alternativeGroupHeader}>
											<CustomBadge variant="info">Grupo Alt. {groupIndex + 1}</CustomBadge>
											<div className={styles.groupActions}>
												<CustomButton
													variant="ghost"
													size="sm"
													startIcon={<Plus size={13} />}
													onClick={() => addAlternativeToGroup(group.id)}
													type="button"
												>
													Producto
												</CustomButton>
												{!group.alternatives[group.alternatives.length - 1]?.isReadOnly && (
													<button
														type="button"
														className={styles.removeGroupBtn}
														onClick={() =>
															removeAlternativeFromGroup(group.id, group.alternatives[group.alternatives.length - 1].id)
														}
														aria-label="Eliminar alternativa"
													>
														<X size={14} />
													</button>
												)}
											</div>
										</div>

										{renderProductRowsHeader()}

										{group.alternatives.map((alternative) => (
											<PhytosanitaryApplicationProductRow
												key={alternative.id}
												readOnly={alternative.isReadOnly || false}
												product={alternative}
												lookups={filteredLookups}
												onUpdate={
													alternative.isReadOnly
														? () => {}
														: (field, value) => updateAlternative(group.id, alternative.id, field, value)
												}
												onRemove={alternative.isReadOnly ? () => {} : () => removeAlternativeFromGroup(group.id, alternative.id)}
												showRemove={!alternative.isReadOnly && (hasReadOnlyAlternatives || editableAlternatives.length > 1)}
												isIrrigation={isIrrigation}
											/>
										))}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</CustomModalForm>
	);
};

export default PhytosanitaryApplicationForm;
